/* eslint-disable react/jsx-props-no-spreading */
import { configure } from 'mobx';
import { ThemeProvider } from 'theme-ui';
import { ReactElement, ReactNode, useEffect, Profiler } from 'react';
import { SessionProvider } from 'next-auth/react';
import { appWithTranslation } from 'next-i18next';
import { DefaultSeo } from 'next-seo';
import Script from 'next/script';
import Head from 'next/head';
import { NextPage } from 'next/types';
import { useRouter } from 'next/router';
import theme from '@styles/theme';
import '@styles/external/nprogress.css';
import '@adyen/adyen-web/dist/adyen.css';
import '@styles/skip-links.css';
import '@features/productPreview/components/TrustMateReviews/omnibus-icons-global.css';
import GlobalStyles from '@styles/theme/GlobalStyles';
import ErrorBoundary from '@components/errors/ErrorBoundary';
import Layout from '@features/layout/components/Layout/Layout';
import { useRouteProgress } from '@hooks/useRouteProgress';
import logger from '@utils/logger';
import { BASE_PATH, REACT_PROFILING } from '@config/index';
import ReaffiliatesHead from '@reaffiliates/ReaffiliatesHead';
import DevToolbar from '@components/DevToolbar/DevToolbar';
import { isDevToolbarAllowed } from '@components/DevToolbar/helpers/isDevToolbarAllowed';
import getPublicEnv from '@utils/getPublicEnv';
import prepareUrlToShare from '@utils/prepareUrlToShare';
import DefaultSeoConfig from '../../next-seo.config';
import type { AppProps } from 'next/app';
configure({
  enforceActions: 'always'
});
type NextPageWithLayout<P = Record<string, unknown>, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement, pageProps: AppProps) => ReactNode;
};
type AppPropsWithLayout = AppProps & {
  // Fix React 19 type compatibility - Component can return ReactNode which includes undefined
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: NextPageWithLayout | any;
};
const App = ({
  Component,
  pageProps
}: AppPropsWithLayout) => {
  const router = useRouter();
  useRouteProgress();
  const isDevToolbarActive = () => {
    if (!isDevToolbarAllowed()) return false;
    const {
      devtoolbar
    } = router.query;
    return devtoolbar && devtoolbar === 'active';
  };
  const getLayout = (page: ReactElement) => Component.getLayout ? Component.getLayout(page, pageProps) : <Layout footer={pageProps.footer} navigationBar={pageProps.navigationBar} categoriesTree={pageProps.categoriesTree} promotionalMessages={pageProps.promotionalMessages} simpleLayout={pageProps.simpleLayout} overflowY={pageProps?.layoutOverflowY} overflowX={pageProps?.layoutOverflowX} attachFirstChildToNavigation={pageProps.attachFirstChildToNavigation} navParams={pageProps.navParams} addresses={pageProps.addresses}>
        {page}
      </Layout>;
  useEffect(() => {
    if (history.scrollRestoration !== 'manual') {
      history.scrollRestoration = 'manual';
    }
  }, []);
  useEffect(() => {
    console.log('REACT_PROFILING getPublicEnv', getPublicEnv('REACT_PROFILING'));
    console.log('REACT_PROFILING:', process.env.REACT_PROFILING);
  }, []);
  const onRenderProfiler = (id: string, phase: 'mount' | 'update' | 'nested-update', actualDuration: number, baseDuration: number, startTime: number, commitTime: number) => {
    // Log profiling metrics
    console.log('[Profiler]', {
      id,
      phase,
      actualDuration,
      baseDuration,
      startTime,
      commitTime
    });
  };
  useEffect(() => {
    const currentUrl = window.location.href;
    const modifiedUrl = prepareUrlToShare(currentUrl);
    if (modifiedUrl !== currentUrl) {
      try {
        window.history.replaceState({}, '', modifiedUrl);
        if (document.title) {
          document.title = document.title + ' ';
        }
      } catch (error) {
        console.warn('History replaceState failed:', error);
      }
    }
  }, [router.asPath]);
  const getPackshot = () => pageProps?.metaData?.image ? pageProps?.metaData?.image : null;
  const overWriteDefaultSeoConfig = () => {
    const packshot = getPackshot();
    if (!packshot) return DefaultSeoConfig();
    const defaultSeoConfig = DefaultSeoConfig();
    defaultSeoConfig.additionalLinkTags.push({
      rel: 'preload',
      href: packshot,
      as: 'image',
      type: 'image/jpeg',
      crossOrigin: ''
    });
    return defaultSeoConfig;
  };
  const appContent = <>
      <Head>
        <meta name="viewport" content="width=device-width,initial-scale=1,minimum-scale=1,maximum-scale=1,user-scalable=no" />
      </Head>
      <ReaffiliatesHead />
      <Script strategy="beforeInteractive" src={`${BASE_PATH}/js/properties.js`} />
      <Script strategy="beforeInteractive" src={`${BASE_PATH}/js/getPublicEnv.js`} />
      <DefaultSeo {...overWriteDefaultSeoConfig()} />
      <SessionProvider session={pageProps.session} basePath={`${BASE_PATH}/api/auth`}>
        <ThemeProvider theme={theme}>
          <GlobalStyles styles />
          <ErrorBoundary>{getLayout(<Component {...pageProps} />)}</ErrorBoundary>
          <div id="modal-root" />
          {isDevToolbarActive() && <DevToolbar pageProps={pageProps} />}
        </ThemeProvider>
      </SessionProvider>
    </>;
  return REACT_PROFILING ?
  // @ts-expect-error - React 19 type compatibility issue with Profiler
  <Profiler id="App" onRender={onRenderProfiler}>
      {appContent}
    </Profiler> : appContent;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function reportWebVitals(metric: any) {
  if (process.env.NODE_ENV !== 'production') logger.info(metric);
}
export default appWithTranslation(App);